import { useMemo } from 'react';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { useProcessStore, getProcessStoreState } from '../store';
import type { ProcessType, Process } from 'shared/domain/process/types/model';
import { groupProcessBadgesByWorkflowId, indexProcessesById, type ProcessBadgeData } from '../model';
import type { UseProcessesResult } from '../types';
import { findById } from 'shared/utils/id/findById';
import { HashMap } from 'shared/types/commonView';

export function useProcessesInitializer(): void {
  useEntityCollectionInitializer(useProcessStore.use.actions());
}

export function useProcesses(): UseProcessesResult {
  const data = useProcessStore.use.data();
  const isReady = useProcessStore.use.isReady();
  const actionStates = useProcessStore.use._actionStates();
  const asyncActions = useProcessStore.use.asyncActions();

  return useMemo(
    () => ({
      processes: data.items,
      loading: data.loading,
      isReady,
      updateWorkflow: {
        execute: asyncActions.updateWorkflow,
        ...actionStates.updateWorkflow,
      },
    }),
    [
      data.items,
      data.loading,
      isReady,
      asyncActions,
      actionStates.updateWorkflow,
    ]
  );
}

export function useProcess(processId: Process | undefined): ProcessType | undefined {
  const data = useProcessStore.use.data();
  return useMemo(
    () => (processId ? findById(data.items, processId, 'useProcess') : undefined),
    [data.items, processId]
  );
}

export function useProcessesLoading(): boolean {
  const data = useProcessStore.use.data();
  return data.loading;
}

export function getProcessById(processId: Process): ProcessType | undefined {
  const { data } = getProcessStoreState();
  return findById(data.items, processId, 'getProcessById');
}

export function getProcesses(): ProcessType[] {
  const { data } = getProcessStoreState();
  return data.items;
}

export function useProcessesObject(): HashMap<ProcessType> {
  const data = useProcessStore.use.data();
  return useMemo(() => indexProcessesById(data.items), [data.items]);
}

export function getProcessesObject(): HashMap<ProcessType> {
  return indexProcessesById(getProcessStoreState().data.items);
}

/** Badges of the processes using each workflow, for workflow lists. */
export function useProcessByWorkflowId(): Record<string, ProcessBadgeData[]> {
  const data = useProcessStore.use.data();
  return useMemo(() => groupProcessBadgesByWorkflowId(data.items), [data.items]);
}
